import { cachedCall } from '@/services/screenCache'
import { api } from './api'

/*
 * «Здравоохранение» — РПН (регистр прикреплённого населения), тот же источник,
 * что у сайта (`stores/rpnHealth.ts`, плитка «Здравоохранение» в «Группах
 * данных»). Решение 29.09.2026: один источник с сайтом.
 *
 * ОДИН ЗАПРОС — `GET /rpn/summary`. До 29.09.2026 агрегирующей ручки у РПН не
 * было, и экран собирался здесь: 13 счётчиков списков с `limit=1` ради
 * `meta.total` и полсотни страниц рождений и смертей, чтобы посчитать месяцы и
 * причины на планшете. Теперь сводку считает бэкенд (`RpnSummaryService`) теми
 * же условиями, что у списков, — числа прежние, а запрос один, и сайт получает
 * ровно тот же ответ.
 *
 * РАНЬШЕ ЭКРАН БЫЛ О ДРУГОМ. `/akim/healthcare` показывал реестр пациентов МЗ
 * (орфанные заболевания, диагнозы, медпоказания) с разрезом по районам. Общих
 * ручек у этого реестра нет, а у РПН нет района: у `/rpn/persons` нет
 * территориального фильтра, у рождений есть только «город / село». Поэтому
 * вкладки «Районы» и фильтра района здесь нет, а вместо диагнозов — рождения,
 * смерти и состав регистра.
 *
 * «ЛЮДИ» — основные карточки (`isMain`: всего карточек 705 870, людей 83 719 —
 * дубли по одному человеку), пол — коды источника 2 (женский) и 3 (мужской).
 *
 * «ДАННЫЕ ПО …» — САМАЯ ПОЗДНЯЯ ДАТА В ВЫГРУЗКЕ (`asOf` сводки), а не сегодня:
 * дамп заливают вручную, и он отстаёт на недели; подпись сегодняшним числом
 * объявила бы этот разрыв нулём рождений и смертей.
 */

type Slice = { label: string; value: number }

/** Часть ответа `GET /rpn/summary`, которую читает экран планшета. */
type RpnSummary = {
  asOf: string | null
  persons: { cards: number; main: number; male: number; female: number; ageBands: Slice[] }
  attachments: { active: number }
  births: { total: number; complete: boolean; byMonth: Slice[]; byLocation: Slice[] }
  deaths: { total: number; complete: boolean; byMonth: Slice[]; byVid: Slice[] }
  perinatal: { total: number }
  network: {
    organizations: number
    pmsp: number
    topOrganizations: Array<{ id: number; name: string; pmsp: boolean }>
  }
}

const MONTHS_RU = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']

const named = (slices: Slice[]) => slices.map((s) => ({ name: s.label, value: s.value }))

export type HealthcareKpi = {
  id: string
  label: string
  value: number | null
  hint?: string
}

export type HealthcareDashboard = {
  asOf: string | null
  kpis: HealthcareKpi[]
  networkKpis: HealthcareKpi[]
  /** Помесячно: `key` — `YYYY-MM`. */
  dynamics: Array<{ key: string; label: string; births: number; deaths: number }>
  ageBands: Array<{ name: string; value: number }>
  sex: Array<{ name: string; value: number }>
  deathCauses: Array<{ name: string; value: number }>
  birthPlaces: Array<{ name: string; value: number }>
  organizations: Array<{ id: number; name: string; pmsp: boolean }>
  /** Выгрузка упёрлась в потолок страниц — числа по месяцам неполные. */
  incomplete: boolean
  sourceNote: string
}

async function fetchHealthcareDashboardRaw(): Promise<HealthcareDashboard> {
  const s = await api.get<RpnSummary>('/rpn/summary')

  // Рождения и смерти по месяцам — в один ряд; месяц без одного из событий
  // получает у него ноль.
  const byMonth = new Map<string, { births: number; deaths: number }>()
  for (const { label, value } of s.births.byMonth) {
    byMonth.set(label, { births: value, deaths: 0 })
  }
  for (const { label, value } of s.deaths.byMonth) {
    const row = byMonth.get(label) ?? { births: 0, deaths: 0 }
    row.deaths = value
    byMonth.set(label, row)
  }

  const births = s.births.total
  const deaths = s.deaths.total
  const perDeath = deaths ? Math.round((births / deaths) * 10) / 10 : null

  return {
    asOf: s.asOf,
    kpis: [
      { id: 'births', label: 'Рождений', value: births, hint: 'за весь период выгрузки' },
      { id: 'deaths', label: 'Смертей', value: deaths, hint: 'за весь период выгрузки' },
      {
        id: 'increase',
        label: 'Естественный прирост',
        value: births - deaths,
        hint: perDeath != null ? `на одну смерть — ${perDeath.toLocaleString('ru-RU')} рождения` : undefined,
      },
      {
        id: 'persons',
        label: 'Людей в регистре',
        value: s.persons.main,
        hint: `основные карточки · всего карточек ${s.persons.cards.toLocaleString('ru-RU')}`,
      },
    ],
    networkKpis: [
      { id: 'attachments', label: 'Действующих прикреплений', value: s.attachments.active },
      // Итог выгрузки и ПМСП по всем строкам — как у сайта (`network` в rpnHealth).
      { id: 'organizations', label: 'Медорганизаций', value: s.network.organizations },
      { id: 'pmsp', label: 'Из них ПМСП', value: s.network.pmsp },
      { id: 'perinatal', label: 'Перинатальных смертей', value: s.perinatal.total, hint: 'за весь период выгрузки' },
    ],
    dynamics: [...byMonth.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-24)
      .map(([key, v]) => ({
        key,
        label: `${MONTHS_RU[Number(key.slice(5, 7)) - 1] ?? key} ${key.slice(2, 4)}`,
        ...v,
      })),
    ageBands: named(s.persons.ageBands),
    sex: [
      { name: 'Мужчины', value: s.persons.male },
      { name: 'Женщины', value: s.persons.female },
    ],
    // Сводка отдаёт разрезы по убыванию — остаётся взять верхушку.
    deathCauses: named(s.deaths.byVid).slice(0, 8),
    birthPlaces: named(s.births.byLocation).slice(0, 6),
    // Неудалённые, сначала ПМСП, затем по названию — сортирует бэкенд.
    organizations: s.network.topOrganizations,
    incomplete: !s.births.complete || !s.deaths.complete,
    sourceNote:
      'РПН — регистр прикреплённого населения, тот же источник, что у сайта (/rpn/summary). ' +
      'Района в источнике нет. Рождения и смерти — по датам события, за весь период выгрузки.',
  }
}

/**
 * Экран целиком — в `screenCache` на час: собранная сводка весит килобайты и
 * переживает перезагрузку вкладки, повторное открытие — без запросов. Кнопка
 * «Обновить» сбрасывает ключ через `invalidateScreenCache`.
 */
export function fetchHealthcareDashboard(): Promise<HealthcareDashboard> {
  return cachedCall('healthcare/dashboard', fetchHealthcareDashboardRaw)
}
